import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import type { RootState } from "@/store"
import { alternarModoTema } from "@/store/tema-slice"
import { useGetLojaAtualQuery, useValidarPinTotemMutation } from "@/api"
import { getPeso, abrirSischefLepeso, balancaDisponivel, type ResultadoPeso } from "@/lib/balanca"
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material"
import ScaleIcon from '@mui/icons-material/Scale'
import CloseIcon from '@mui/icons-material/Close'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import NumericKeypad from "./components/numeric-keypad"
import PrintIcon from '@mui/icons-material/Print'
import { SelectLocalImpressao } from "@/components/select-local-impressao"
import { getLocalImpressaoTotem, setLocalImpressaoTotem } from "@/lib/totem-config"
import { imprimeNoAparelho } from "@/lib/impressao-totem"
import { onAbrirConfigDispositivo } from "@/lib/tablet-eventos"
import { urlTrocarPontoTotem } from "@/lib/url-pdv"
import { soltarUrlDoAparelho } from "@/lib/vinculo-aparelho"
import { PreferenciasAparelhoTotem } from "@/components/totem/preferencias-aparelho"
import { AtivacaoTefTotem } from "@/components/totem/ativacao-tef"
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ContrastIcon from '@mui/icons-material/Contrast'
import AirplayIcon from '@mui/icons-material/Airplay'

type Fase = 'pin' | 'config'

function descreverResultado(r: ResultadoPeso): string {
    switch (r.status) {
        case 'PESO':
            return `✓ Peso lido: ${r.pesoKg.toFixed(3)} kg`
        case 'INSTAVEL':
            return `Peso instável: ${r.pesoKg.toFixed(3)} kg (aguarde estabilizar)`
        case 'VAZIO':
            return 'Balança vazia (sem peso).'
        case 'ERRO':
            return `Erro ${r.code}: ${r.message}`
    }
}

function ConfigTotemCheckout(): JSX.Element | null {
    const dispatch = useDispatch()
    const modoTema = useSelector((s: RootState) => s.tema.mode)
    const { data: loja } = useGetLojaAtualQuery()
    const [validarPin, statusPin] = useValidarPinTotemMutation()
    const [aberto, setAberto] = useState(false)
    const [fase, setFase] = useState<Fase>('pin')
    const [pin, setPin] = useState('')
    const [erro, setErro] = useState<string | null>(null)
    const [localImpressao, setLocalImpressao] = useState('')
    const [testando, setTestando] = useState(false)
    const [resultado, setResultado] = useState<string | null>(null)
    const [confirmandoTroca, setConfirmandoTroca] = useState(false)
    const ehCheckout = loja?.identificador.tipo === 'TOTEM_CHECKOUT'
    const balancaLigada = loja?.configuracao.totemBalancaAtiva === true && balancaDisponivel()

    const handleClose = (_e: any, reason: any) => {
        if (reason !== 'backdropClick') setAberto(false)
    }

    function abrir(): void {
        setErro(null)
        setResultado(null)
        setPin('')
        setFase('pin')
        setConfirmandoTroca(false)
        setLocalImpressao(getLocalImpressaoTotem())
        setAberto(true)
    }

    async function confirmarPin(): Promise<void> {
        setErro(null)
        try {
            const response = await validarPin({ pin: pin.trim() }).unwrap()

            if (response.ok) {
                setFase('config')
            } else {
                setErro('PIN incorreto.')
            }
        } catch {
            setErro('Não foi possível validar o PIN. Tente novamente.')
        }
    }

    async function testarBalanca(): Promise<void> {
        setTestando(true)
        setResultado(null)

        try {
            setResultado(descreverResultado(await getPeso()))
        } finally {
            setTestando(false)
        }
    }

    useEffect(() => onAbrirConfigDispositivo(() => abrir()), [])

    if (!ehCheckout) return null

    return (
        <Dialog
            open={aberto}
            onClose={handleClose}
            fullWidth
            maxWidth={fase === 'pin' ? "xs" : "sm"}
        >
            <DialogTitle sx={{ p: 2 }}>
                <Stack direction={'row'} sx={{ alignItems: 'start', justifyContent: 'space-between' }}>
                    <Typography variant="h6">
                        {fase === 'pin' ? '' : 'Configurações'}
                    </Typography>

                    <IconButton onClick={() => setAberto(false)} sx={{ p: 0 }}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 2 }}>
                {erro && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {erro}
                    </Alert>
                )}

                {fase === 'pin' ? (
                    <Stack spacing={3} >
                        <Box
                            sx={{
                                textAlign: 'center',
                            }}
                        >
                            {pin ? (
                                <Typography variant="h3">
                                    {'•'.repeat(pin.length)}
                                </Typography>
                            ) : (
                                <Typography variant="h3" color="text.secondary">
                                    Digite o PIN
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <NumericKeypad
                                onDigito={(d) => setPin((p) => (p + d).slice(0, 8))}
                                onApagar={() => setPin((p) => p.slice(0, -1))}
                                onConfirmar={() => {
                                    if (pin.trim() && !statusPin.isLoading) void confirmarPin()
                                }}
                                apagarDesabilitado={!pin}
                                confirmarDesabilitado={!pin.trim() || statusPin.isLoading}
                            />
                        </Box>
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        {!imprimeNoAparelho() && (
                        <Stack spacing={1}>
                            <Stack spacing={1} direction={'row'} sx={{ alignItems: 'center' }}>
                                <List sx={{ width: '100%', p: 0 }}>
                                    <ListItem sx={{ pl: 1, py: 0 }}>
                                        <ListItemIcon sx={{ minWidth: '48px' }}>
                                            <PrintIcon sx={{ fontSize: '30px' }} />
                                        </ListItemIcon>

                                        <ListItemText
                                            primary="Impressora"
                                            secondary="Onde saem o comprovante do cartão e o cupom da NFC-e deste totem"
                                            slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                        />
                                    </ListItem>
                                </List>
                            </Stack>

                            <SelectLocalImpressao
                                pin={pin.trim()}
                                valor={localImpressao}
                                onChange={(valor) => {
                                    setLocalImpressao(valor)
                                    setLocalImpressaoTotem(valor)
                                }}
                                ajuda="Impressora acoplada a este totem."
                            />

                        </Stack>
                        )}

                        <Stack spacing={1} sx={{ pt: 1 }}>
                            <Stack spacing={1}>
                                <Stack spacing={1} direction={'row'} sx={{ alignItems: 'center' }}>
                                    <List sx={{ width: '100%', p: 0 }}>
                                        <ListItem sx={{ pl: 1, py: 0 }}>
                                            <ListItemIcon sx={{ minWidth: '48px' }}>
                                                <ContrastIcon sx={{ fontSize: '30px' }} />
                                            </ListItemIcon>

                                            <ListItemText
                                                primary="Tema do aplicativo"
                                                secondary="Escolha entre tema claro ou escuro para o aplicativo do totem."
                                                slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                            />
                                        </ListItem>
                                    </List>
                                </Stack>

                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={modoTema === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                                    sx={{ textTransform: 'none' }}
                                    onClick={() => dispatch(alternarModoTema())}
                                >
                                    {modoTema === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
                                </Button>
                            </Stack>

                            <PreferenciasAparelhoTotem />

                            <AtivacaoTefTotem />

                            {urlTrocarPontoTotem() && (
                                <>
                                    <Stack spacing={1} direction={'row'} sx={{ alignItems: 'center', pt: 2 }}>
                                        <List sx={{ width: '100%', p: 0 }}>
                                            <ListItem sx={{ pl: 1, py: 0 }}>
                                                <ListItemIcon sx={{ minWidth: '48px' }}>
                                                    <AirplayIcon sx={{ fontSize: '30px' }} />
                                                </ListItemIcon>

                                                <ListItemText
                                                    primary="Ponto de acesso"
                                                    secondary="Quando clicado, você pode trocar para outro ponto de acesso."
                                                    slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                                />
                                            </ListItem>
                                        </List>
                                    </Stack>

                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<SwapHorizIcon />}
                                        sx={{ textTransform: 'none' }}
                                        onClick={() => setConfirmandoTroca(true)}
                                    >
                                        Trocar ponto de acesso
                                    </Button>

                                    {confirmandoTroca && (
                                        <Alert
                                            severity="warning"
                                            action={
                                                <Button
                                                    size="small"
                                                    color="inherit"
                                                    onClick={() => void soltarUrlDoAparelho().then(() => { window.location.href = urlTrocarPontoTotem()! })}
                                                >
                                                    Trocar
                                                </Button>
                                            }
                                        >
                                            Este totem sai do ponto de acesso atual e volta a pedir um codigo de
                                            pareamento. Tenha o codigo novo em maos antes de continuar.
                                        </Alert>
                                    )}
                                </>
                            )}
                        </Stack>

                        {balancaLigada && (
                            <>
                                <Stack spacing={1} direction={'row'} sx={{ alignItems: 'center', pt: 2 }}>
                                    <List sx={{ width: '100%', p: 0 }}>
                                        <ListItem sx={{ pl: 1, py: 0 }}>
                                            <ListItemIcon sx={{ minWidth: '48px' }}>
                                                <ScaleIcon sx={{ fontSize: '30px' }} />
                                            </ListItemIcon>

                                            <ListItemText
                                                primary="Balança"
                                                secondary="Configurações e teste da balança para autopesagem"
                                                slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                            />
                                        </ListItem>
                                    </List>
                                </Stack>

                                <Stack spacing={1}>
                                    <Button variant="contained" onClick={testarBalanca} disabled={testando} sx={{ textTransform: 'none' }}>
                                        {testando ? <CircularProgress size={20} /> : 'Testar balança (ler peso)'}
                                    </Button>

                                    {resultado && (
                                        <Alert severity={resultado.startsWith('✓') ? 'success' : 'info'}>{resultado}</Alert>
                                    )}

                                    <Button variant="outlined" onClick={() => void abrirSischefLepeso()} sx={{ textTransform: 'none' }}>
                                        Abrir SischefLepeso (configurar balança)
                                    </Button>
                                </Stack>
                            </>
                        )}
                    </Stack>
                )}
            </DialogContent>
        </Dialog >
    )
}

export default ConfigTotemCheckout
